import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import type { RouteConfig, RouteGuardResult, UserRole } from '../../types/navigation';
import type { UserProfile } from '../../types/api/auth';

/**
 * Protected Route Component with Role-Based Access Control
 * 
 * Features:
 * - Authentication verification
 * - Role-based access control
 * - Automatic redirects
 * - Loading states
 * - Error boundaries
 */
interface ProtectedRouteProps {
  config: RouteConfig;
  children: React.ReactNode;
}

export function ProtectedRoute({ config, children }: ProtectedRouteProps): React.JSX.Element {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const location = useLocation();


  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check route access permissions
  const guardResult = checkRouteAccess(config, user, isAuthenticated);

  if (!guardResult.canAccess) {
    // Redirect to appropriate page based on the reason
    if (!isAuthenticated) {
      // Redirect to login with return URL
      return <Navigate to={`/auth/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
    } else if (guardResult.redirectTo) {
      return <Navigate to={guardResult.redirectTo} replace />;
    } else {
      // Redirect to unauthorized page
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

/**
 * Get user role from profile
 */
function getUserRole(user: UserProfile | null): UserRole {
  if (!user) return 'CUSTOMER';
  
  // Check if it's a merchant profile
  if ('merchantProfileId' in user) {
    return 'MERCHANT';
  }
  
  // Check if it's an admin profile
  if ('adminProfileId' in user) {
    if ('accessLevel' in user && typeof user.accessLevel === 'string') {
      return user.accessLevel as UserRole;
    }
    return 'ADMIN';
  }
  
  // Check if it's a regular user
  if ('role' in user) {
    return user.role as UserRole;
  }
  
  // Default fallback
  return 'CUSTOMER';
}

/**
 * Check if user has access to a specific route
 */
function checkRouteAccess(
  config: RouteConfig,
  user: UserProfile | null,
  isAuthenticated: boolean
): RouteGuardResult {
  // Public routes are accessible to everyone
  if (config.accessLevel === 'public') {
    return { canAccess: true };
  }

  // Authenticated routes require user to be logged in
  if (config.accessLevel === 'authenticated') {
    if (!isAuthenticated || !user) {
      return { 
        canAccess: false, 
        reason: 'Authentication required' 
      };
    }
    return { canAccess: true };
  }

  // Role-specific routes require specific user roles
  if (config.accessLevel === 'role-specific') {
    if (!isAuthenticated || !user) {
      return { 
        canAccess: false, 
        reason: 'Authentication required' 
      };
    }

    if (!config.allowedRoles || config.allowedRoles.length === 0) {
      return { 
        canAccess: false, 
        reason: 'No roles specified for this route' 
      };
    }

    const userRole = getUserRole(user);
    if (!config.allowedRoles.includes(userRole)) {
      return { 
        canAccess: false, 
        reason: 'Insufficient permissions',
        redirectTo: getDefaultRouteForRole(userRole)
      };
    }

    return { canAccess: true };
  }

  return { canAccess: false, reason: 'Unknown access level' };
}

/**
 * Get default route for a user role
 */
function getDefaultRouteForRole(role: UserRole): string {
  switch (role) {
    case 'CUSTOMER':
      return '/customer/dashboard';
    case 'MERCHANT':
      return '/merchant/dashboard';
    case 'RESTAURANT_OWNER':
      return '/restaurant/dashboard';
    case 'DELIVERY_DRIVER':
      return '/driver/dashboard';
    case 'ADMIN':
    case 'SUPER_ADMIN':
    case 'CONTENT_MANAGER':
    case 'USER_SUPPORT':
    case 'ANALYST':
    case 'MODERATOR':
      return '/home';
    default:
      return '/';
  }
}

/**
 * Higher-order component for creating protected routes
 */
export function withProtectedRoute<T extends object>(
  Component: React.ComponentType<T>,
  config: RouteConfig
) {
  return function ProtectedComponent(props: T): React.JSX.Element {
    return (
      <ProtectedRoute config={config}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}

/**
 * Hook for checking route permissions
 */
export function useRoutePermission(config: RouteConfig): RouteGuardResult {
  const { user, isAuthenticated } = useAuthContext();
  return checkRouteAccess(config, user, isAuthenticated);
}
